/**
 * levelgen/reachability.ts — Can the walker actually get there?
 *
 * The one question a generated level has to answer before anyone plays it. A
 * town that looks perfect and has the bin behind a terrace with no way in is
 * not a hard level, it's a broken one, and no amount of difficulty tuning makes
 * it playable.
 *
 * ## How it's answered
 *
 * Coarse grid, obstacles grown by the walker's radius, flood fill. That is the
 * standard trick and it is worth spelling out why the growing comes first: a
 * walker is a **disc**, not a point, so a 20-unit gap between two houses is
 * impassable to a 16-radius player even though a point-sized flood fill sails
 * straight through it. Inflating every obstacle by the radius turns the disc
 * back into a point and makes the naive fill correct again.
 *
 * ## Which way it errs
 *
 * Toward saying "no". A cell is blocked if it touches an inflated obstacle
 * *anywhere*, not merely at its centre, so a cell that is half free counts as
 * solid. That means the fill can call a level unreachable when a player could
 * in fact squeeze through — which costs a seed, and nothing else. The opposite
 * error would ship an unfinishable level, so the bias is deliberately this way
 * round.
 */
import type { Rect } from '../level';

/**
 * A walkability grid over the ground the walk happens on.
 *
 * Not over the whole map. A generated town carries on past the playable
 * rectangle as scenery (see `levelgen/surround.ts`), and that ring is several
 * times the area of the town inside it — gridding all of it would spend most of
 * the fill proving things about ground the walker is clamped out of.
 *
 * So the grid has an **origin**, and every world point is converted through it.
 */
export interface WalkGrid {
  /** World position of the grid's top-left corner. */
  x: number;
  y: number;
  cols: number;
  rows: number;
  /** World units per cell. */
  cell: number;
  /** Row-major, 1 = blocked. */
  blocked: Uint8Array;
}

/**
 * Cell size.
 *
 * Small enough to resolve the tightest gap the game cares about — the walker is
 * 32 across, so anything finer than about half that is measuring noise — and
 * large enough that a big very-hard map is still a few thousand cells rather
 * than a few hundred thousand. `validate.ts` runs two fills per level and the
 * generator may try several seeds, so this is on the hot path of a test sweep.
 */
export const CELL = 8;

/**
 * Build the grid.
 *
 * @param area       the ground the walk happens on (`LevelSpec.bounds`)
 * @param obstacles  solid rects (building plots), in world coordinates
 * @param circles    solid circles (tree trunks), in world coordinates
 * @param radius     the walker's radius; everything is grown by it
 */
export function buildWalkGrid(
  area: Rect,
  obstacles: readonly Rect[],
  circles: readonly { x: number; y: number; r: number }[],
  radius: number,
  cell = CELL,
): WalkGrid {
  const cols = Math.ceil(area.w / cell);
  const rows = Math.ceil(area.h / cell);
  const blocked = new Uint8Array(cols * rows);

  // The boundary is solid too: `clampToBounds` holds the walker's whole circle
  // inside it, so the outermost `radius` is not walkable ground. That is as
  // true of the edge of the walk as it was of the edge of the map — the town
  // beyond it is drawn, but it is not somewhere anybody can go.
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const x0 = i * cell;
      const y0 = j * cell;
      if (x0 < radius || y0 < radius || x0 + cell > area.w - radius || y0 + cell > area.h - radius) {
        blocked[j * cols + i] = 1;
      }
    }
  }

  // Obstacles arrive in world coordinates and the grid is local to `area`, so
  // everything is shifted onto the grid's own origin on the way in. Anything
  // out in the surround simply falls off the edge of it, which `fillRect`
  // already clamps away.
  for (const o of obstacles) {
    fillRect(blocked, cols, rows, cell, {
      x: o.x - radius - area.x,
      y: o.y - radius - area.y,
      w: o.w + radius * 2,
      h: o.h + radius * 2,
    });
  }

  // A trunk is grown to a square rather than tested as a circle. Over-blocking
  // by the corners of a 20-unit box is beneath the resolution that matters, and
  // it keeps the fill on the safe side of the bias described above.
  for (const c of circles) {
    const r = c.r + radius;
    fillRect(blocked, cols, rows, cell, {
      x: c.x - r - area.x,
      y: c.y - r - area.y,
      w: r * 2,
      h: r * 2,
    });
  }

  return { x: area.x, y: area.y, cols, rows, cell, blocked };
}

/** Mark every cell the rect touches. */
function fillRect(
  blocked: Uint8Array,
  cols: number,
  rows: number,
  cell: number,
  r: Rect,
): void {
  const i0 = Math.max(0, Math.floor(r.x / cell));
  const j0 = Math.max(0, Math.floor(r.y / cell));
  const i1 = Math.min(cols - 1, Math.ceil((r.x + r.w) / cell) - 1);
  const j1 = Math.min(rows - 1, Math.ceil((r.y + r.h) / cell) - 1);
  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) blocked[j * cols + i] = 1;
  }
}

/** Whether the cell containing a world point is walkable. */
export function isOpen(grid: WalkGrid, x: number, y: number): boolean {
  const i = Math.floor((x - grid.x) / grid.cell);
  const j = Math.floor((y - grid.y) / grid.cell);
  if (i < 0 || j < 0 || i >= grid.cols || j >= grid.rows) return false;
  return grid.blocked[j * grid.cols + i] === 0;
}

/**
 * Every cell reachable on foot from `from`, as a mask.
 *
 * Four-connected, not eight: a diagonal step between two blocked cells would
 * squeeze the walker through a corner they can't actually get through, which is
 * the classic way a grid fill lies about a tight alley.
 */
export function floodFrom(grid: WalkGrid, from: { x: number; y: number }): Uint8Array {
  const seen = new Uint8Array(grid.cols * grid.rows);
  const start = cellIndex(grid, from);
  if (start < 0 || grid.blocked[start]) return seen;

  // An explicit stack rather than recursion: a big map is tens of thousands of
  // cells deep in the worst case, which is a stack overflow waiting to happen.
  const stack = [start];
  seen[start] = 1;
  while (stack.length) {
    const at = stack.pop() as number;
    const i = at % grid.cols;
    const j = (at - i) / grid.cols;
    const neighbours = [
      i > 0 ? at - 1 : -1,
      i < grid.cols - 1 ? at + 1 : -1,
      j > 0 ? at - grid.cols : -1,
      j < grid.rows - 1 ? at + grid.cols : -1,
    ];
    for (const n of neighbours) {
      if (n < 0 || seen[n] || grid.blocked[n]) continue;
      seen[n] = 1;
      stack.push(n);
    }
  }
  return seen;
}

/** Index of the cell holding a world point, or −1 if it's outside the grid. */
function cellIndex(grid: WalkGrid, p: { x: number; y: number }): number {
  const i = Math.floor((p.x - grid.x) / grid.cell);
  const j = Math.floor((p.y - grid.y) / grid.cell);
  if (i < 0 || j < 0 || i >= grid.cols || j >= grid.rows) return -1;
  return j * grid.cols + i;
}

/**
 * Whether any walkable cell within `radius` of `to` is in the reached set.
 *
 * A radius rather than a point, because that is how the objectives actually
 * work: the bag is binned anywhere within `OBJECTIVE.BIN_RADIUS` of the bin, so
 * reaching the bin means reaching its *drop zone*. Standing on the bin itself
 * is impossible — it's a solid object.
 */
export function reachedNear(
  grid: WalkGrid,
  reached: Uint8Array,
  to: { x: number; y: number },
  radius: number,
): boolean {
  const i0 = Math.max(0, Math.floor((to.x - radius - grid.x) / grid.cell));
  const j0 = Math.max(0, Math.floor((to.y - radius - grid.y) / grid.cell));
  const i1 = Math.min(grid.cols - 1, Math.floor((to.x + radius - grid.x) / grid.cell));
  const j1 = Math.min(grid.rows - 1, Math.floor((to.y + radius - grid.y) / grid.cell));

  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const cx = grid.x + (i + 0.5) * grid.cell;
      const cy = grid.y + (j + 0.5) * grid.cell;
      if (Math.hypot(cx - to.x, cy - to.y) > radius) continue;
      if (reached[j * grid.cols + i]) return true;
    }
  }
  return false;
}

/**
 * Whether any walkable cell whose **centre** lies inside `area` is in the
 * reached set.
 *
 * Centres rather than any touched cell, because the caller is asking whether
 * the player can stand in a trigger zone, and a cell that merely clips the
 * zone's edge doesn't prove that. Same bias as everywhere else here: err toward
 * "no".
 */
export function reachedInside(grid: WalkGrid, reached: Uint8Array, area: Rect): boolean {
  const i0 = Math.max(0, Math.floor((area.x - grid.x) / grid.cell));
  const j0 = Math.max(0, Math.floor((area.y - grid.y) / grid.cell));
  const i1 = Math.min(grid.cols - 1, Math.floor((area.x + area.w - grid.x) / grid.cell));
  const j1 = Math.min(grid.rows - 1, Math.floor((area.y + area.h - grid.y) / grid.cell));

  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const cx = grid.x + (i + 0.5) * grid.cell;
      const cy = grid.y + (j + 0.5) * grid.cell;
      if (cx < area.x || cx > area.x + area.w || cy < area.y || cy > area.y + area.h) continue;
      if (reached[j * grid.cols + i]) return true;
    }
  }
  return false;
}
